import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { JobStatus } from '@/lib/types'
import { api } from '@/lib/api'
import { getJob, listJobs, updateJob, assignJob, getJobMedia, createJob } from './api'

export function useJob(jobId: string) {
  return useQuery({
    queryKey: ['job', jobId],
    queryFn: () => getJob(jobId),
    enabled: !!jobId,
  })
}

// Photos attached to a job — fetched on demand when a job detail is open.
export function useJobMedia(jobId: string | null | undefined) {
  return useQuery({
    queryKey: ['job-media', jobId],
    queryFn: () => getJobMedia(jobId!),
    enabled: !!jobId,
  })
}

export function useJobs(
  accountId: string,
  filters?: { status?: JobStatus; statuses?: JobStatus[]; customerId?: string; assignedUserId?: string; cursor?: string; limit?: number; order?: 'fifo' | 'lifo' },
  options?: { refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: ['jobs', accountId, filters],
    queryFn: () => listJobs(accountId, filters),
    enabled: !!accountId,
    refetchInterval: options?.refetchInterval,
  })
}

/**
 * Jobs for a single customer — backs the Conversations contact card's Job(s)
 * section. Disabled until both ids are known so we never fetch the account's
 * full job list by accident.
 */
export function useCustomerJobs(accountId: string, customerId: string | null | undefined) {
  return useQuery({
    queryKey: ['jobs', accountId, { customerId, limit: 50 }],
    queryFn: () => listJobs(accountId, { customerId: customerId!, limit: 50 }),
    enabled: !!accountId && !!customerId,
  })
}

// Create a job manually (contractor portal). Jobs are usually created by the SMS
// assistant; this backs the sidebar Create launcher's "Job" option. Invalidates
// the jobs list so the new job shows in the queue feed immediately.
export function useCreateJob() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Parameters<typeof createJob>[0]) => createJob(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
    },
  })
}

export function useUpdateJob() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      jobId,
      updates,
    }: {
      jobId: string
      updates: Parameters<typeof updateJob>[1]
    }) => updateJob(jobId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      // The /queue job view reads the single-job query — keep it in step.
      queryClient.invalidateQueries({ queryKey: ['job'] })
    },
  })
}

// Claim / release / reassign a job's owner. Invalidates the jobs cache so the
// list + detail reflect the new owner.
export function useAssignJob() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId, userId }: { jobId: string; userId: string | null }) =>
      assignJob(jobId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      queryClient.invalidateQueries({ queryKey: ['job'] })
    },
  })
}

export type TeamMember = {
  id: string
  name: string
  role: 'OWNER' | 'MEMBER'
  status: string
  /** E.164 or null — the number a call-handling escalation would dial. */
  mobilePhone?: string | null
}

/**
 * Active team members for the reassign picker. GET /team/users is OWNER-gated,
 * so only fetch when the caller is an owner (members can't reassign anyway).
 */
export function useTeamMembers(enabled: boolean) {
  return useQuery({
    queryKey: ['team-users'],
    queryFn: async () => {
      const users = await api<TeamMember[]>('/team/users')
      return users.filter((u) => u.status === 'ACTIVE')
    },
    enabled,
  })
}
